import * as React from "react";
import {observer} from "mobx-react-lite";
import {
    CAlert,
    CBody1, CBody2,
    CBox,
    CButton,
    CCard,
    CCardContent,
    CCircularProgress,
    CDialog, CDialogActions,
    CDialogContent,
    CDialogTitle, CFormControl,
    CGrid, CGridContainer, CH4, CH6, CInputLabel, CMenuItem, CSelect,
    CSnackbar,
    CTextField,
} from "../../components";
import {hasValue} from "../../utils/empty-check";
import {useStores} from "../../models";
import {useEffect} from "react";
import {AlertColor, IconButton, SelectChangeEvent} from "@mui/material";
import {
    AccountBalanceWalletOutlined,
    ContentCopyOutlined, KeyboardCommandKeyOutlined,
    KeyOutlined
} from "@mui/icons-material";
import {isEmpty} from "validate.js";
import {Project} from "../../models/project/project";
import {Staff} from "../../models/staff/staff";

export const Projects = observer(
    function Projects() {

        const {authStore, projectStore, businessStore} = useStores()
        const {business} = businessStore
        const {
            getProjects,
            projects,
            isLoadingProjects,
            updateProject,
            deleteProject,
            revokeProjectApiKey
        } = projectStore
        const {
            staff
        } = authStore

        const [snackBar, setSnackBar] = React.useState<{
            severity: AlertColor,
            message: string,
            isOpen: boolean
        }>({
            severity: "success",
            message: "",
            isOpen: false
        });
        const [dialog, setDialog] = React.useState<{
            isOpen: boolean,
            isAdd: boolean,
            data: {
                id?: string,
                name: string,
                walletAddress: string
            } | null
        }>({
            isOpen: false,
            isAdd: false,
            data: null
        });

        const [revokeDialog, setRevokeDialog] = React.useState<{
            isOpen: boolean,
            data: {
                id: string
            } | null
        }>({
            isOpen: false,
            data: null
        });

        const [deleteDialog, setDeleteDialog] = React.useState<{
            isOpen: boolean,
            data: {
                id: string
            } | null
        }>({
            isOpen: false,
            data: null
        });

        const [sdkDialog, setSDKDialog] = React.useState<{
            isOpen: boolean,
            platform: string,
            data: Project | null
        }>({
            isOpen: false,
            platform: "Flutter",
            data: null
        });

        const [fundDialog, setFundDialog] = React.useState<{
            isOpen: boolean,
            data: Project | null
        }>({
            isOpen: false,
            data: null
        });

        const [formData, setFormData] = React.useState({
            name: "",
            walletAddress: ""
        })
        const [isUpdating, setIsUpdating] = React.useState(false);
        const [formDataValidationMessages, setFormDataValidationMessages] = React.useState({
            name: "",
            walletAddress: ""
        })

        const showSnackBar = (message: string, severity: AlertColor) => {
            setSnackBar({
                severity: severity,
                message: message,
                isOpen: true
            });
        };

        const closeSnackbar = (event?: React.SyntheticEvent | Event, reason?: string) => {
            if (reason === 'clickaway') {
                return;
            }

            setSnackBar({
                severity: "success",
                message: "",
                isOpen: false
            });
        };

        const openUpdateDialog = (data) => {
            setFormData({
                name: data.name,
                walletAddress: data.walletAddress
            })
            setFormDataValidationMessages({
                name: "",
                walletAddress: ""
            })
            setDialog({
                isOpen: true,
                isAdd: false,
                data
            })
        }

        const closeUpdateDialog = () => {
            setDialog({
                isOpen: false,
                isAdd: false,
                data: null
            })
            setFormData({
                name: "",
                walletAddress: ""
            })
            setFormDataValidationMessages({
                name: "",
                walletAddress: ""
            })
        }

        const openAddDialog = () => {
            setFormData({
                name: "",
                walletAddress: ""
            })
            setFormDataValidationMessages({
                name: "",
                walletAddress: ""
            })
            setDialog({
                isOpen: true,
                isAdd: true,
                data: null
            })
        }

        const closeAddDialog = () => {
            setDialog({
                isOpen: false,
                isAdd: true,
                data: null
            })
            setFormData({
                name: "",
                walletAddress: ""
            })
            setFormDataValidationMessages({
                name: "",
                walletAddress: ""
            })
        }

        const openRevokeDialog = (id: string | undefined) => {
            if (id) {
                setRevokeDialog({
                    isOpen: true,
                    data: {
                        id
                    }
                })
            }
        }

        const closeRevokeDialog = () => {
            setRevokeDialog({
                isOpen: false,
                data: null
            })
        }

        const openDeleteDialog = (id: string | undefined) => {
            if (id) {
                setDeleteDialog({
                    isOpen: true,
                    data: {
                        id
                    }
                })
            }

        }

        const closeDeleteDialog = () => {
            setDeleteDialog({
                isOpen: false,
                data: null
            })
        }

        const openSDKDialog = (project) => {
            setSDKDialog({
                isOpen: true,
                platform: "Flutter",
                data: project
            })

        }

        const closeSDKDialog = () => {
            setSDKDialog({
                isOpen: false,
                platform: "Flutter",
                data: null
            })
        }

        const openFundDialog = (project) => {
            setFundDialog({
                isOpen: true,
                data: project
            })

        }

        const closeFundDialog = () => {
            setFundDialog({
                isOpen: false,
                data: null
            })
        }




        useEffect(() => {
            if (business?.id) {
                getProjects(business.id, staff as Staff)
            }
        }, [])

        const isValid = () => {

            let output = true

            const currentFormDataValidationMessages = {
                ...formDataValidationMessages
            }

            if (isEmpty(formData.name)) {
                currentFormDataValidationMessages.name = "Name is required."
                output = false
            }

            if (isEmpty(formData.walletAddress)) {
                currentFormDataValidationMessages.walletAddress = "Wallet Address is required."
                output = false
            }

            if (!output) {
                setFormDataValidationMessages(currentFormDataValidationMessages)
            }

            return output
        }


        const update = async () => {
            if (isValid()) {
                if (formData && dialog.data) {
                    setIsUpdating(true);
                    await updateProject(dialog.data.id, {...dialog.data, ...formData}, business?.id, staff as Staff);
                    setIsUpdating(false)
                }
                closeUpdateDialog();
            }
        }

        const revokeApiKey = async () => {
            setIsUpdating(true);
            await revokeProjectApiKey(revokeDialog.data?.id, business?.id, staff as Staff);
            setIsUpdating(false)

            closeRevokeDialog();
        }

        const submitDelete = async () => {
            setIsUpdating(true);
            await deleteProject(deleteDialog.data?.id, business?.id, staff as Staff);
            setIsUpdating(false)

            closeDeleteDialog();
        }

        const add = async () => {
            if (isValid()) {
                if (formData && businessStore.business) {
                    setIsUpdating(true);
                    await projectStore.addProject(formData, businessStore.business.id, false, staff as Staff);
                    setIsUpdating(false)
                }
                closeAddDialog();
            }
        }

        return (
            <>
                <CGrid container spacing={2}>
                    <CGrid item xs={12}>
                        <CCard>
                            <CCardContent sx={{display: "flex", flexDirection: 'row', alignItems: 'center', py: 3}}>
                                <CH4 fontWeight={'900'}>
                                    Projects
                                </CH4>
                                {staff?.role === "owner" && <CButton sx={{ml: 2}} size="medium" variant="contained" onClick={openAddDialog}>
                                    Add New
                                </CButton>}
                            </CCardContent>
                        </CCard>
                    </CGrid>
                    <CGrid item xs={12}>
                        {isLoadingProjects && (
                            <CBox
                                sx={{
                                    width: '100%',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    padding: 4,
                                    display: 'flex'
                                }}>
                                <CCircularProgress/>
                                <CBody1 sx={{ml: 2, fontWeight: 'bold', opacity: 0.6}}>
                                    Loading Projects ...
                                </CBody1>
                            </CBox>
                        )}

                        {(!isLoadingProjects && hasValue(projects)) && projects?.map(project => {
                            return (
                                <CCard key={project.id} sx={{mb: 2}}>
                                    <CCardContent>
                                        <CGridContainer spacing={2}>
                                            <CGrid item xs={12}>
                                                <CH6 fontWeight={'bold'}>
                                                    {project.name}
                                                </CH6>
                                            </CGrid>
                                            <CGrid item xs={12} display={"flex"} flexDirection={"row"}
                                                   alignItems={"center"}>
                                                <AccountBalanceWalletOutlined/>
                                                <CBody1 sx={{ml: 1}} width={120}>
                                                    Wallet Address:
                                                </CBody1>
                                                <CBody2 sx={{ml: 1, backgroundColor: "#EEEEEE", p: 1, borderRadius: 2}}>
                                                    {project.walletAddress}
                                                    <IconButton size={"small"} sx={{ml: 1}} aria-label="copy"
                                                                onClick={async () => {
                                                                    await navigator.clipboard.writeText(project.walletAddress)
                                                                    showSnackBar("Wallet Address is copied successfully!", "success")
                                                                }}>
                                                        <ContentCopyOutlined fontSize={"small"}/>
                                                    </IconButton>
                                                </CBody2>


                                            </CGrid>
                                            <CGrid item xs={12} display={"flex"} flexDirection={"row"}
                                                   alignItems={"center"}>
                                                <KeyOutlined/>
                                                <CBody1 sx={{ml: 1}} width={120}>
                                                    Api Key:
                                                </CBody1>
                                                <CBody2 sx={{ml: 1, backgroundColor: "#EEEEEE", p: 1, borderRadius: 2}}>
                                                    {project.apiKey}
                                                    <IconButton size={"small"} sx={{ml: 1}} aria-label="copy"
                                                                onClick={async () => {
                                                                    await navigator.clipboard.writeText(project.apiKey)
                                                                    showSnackBar("Api Key is copied successfully!", "success")
                                                                }}>
                                                        <ContentCopyOutlined fontSize={"small"}/>
                                                    </IconButton>
                                                </CBody2>

                                            </CGrid>
                                            <CGrid item xs={12} display={"flex"} justifyContent={"flex-end"}>
                                                {authStore.staff?.role === "owner" && <CButton sx={{mr: 1}} size="medium" variant="contained"
                                                          onClick={() => {
                                                              openFundDialog(project)
                                                          }}>
                                                    Fund
                                                </CButton>}
                                                {authStore.staff?.role === "owner" && <CButton sx={{mr: 1}} size="medium" variant="contained"
                                                          onClick={() => {
                                                              openUpdateDialog(project)
                                                          }}>
                                                    Edit
                                                </CButton>}
                                                <CButton sx={{mr: 1}} size="medium" variant="contained"
                                                         onClick={() => {
                                                             openSDKDialog(project)
                                                         }}>
                                                    SDK Setup
                                                </CButton>
                                                {authStore.staff?.role === "owner" && <CButton sx={{mr: 1}} size="medium" variant="contained"
                                                          onClick={() => {
                                                              openRevokeDialog(project.id)
                                                          }}>
                                                    Revoke Api Key
                                                </CButton>}
                                                {authStore.staff?.role === "owner" && <CButton size="medium" variant="contained" color={"error"}
                                                          onClick={() => {
                                                              openDeleteDialog(project.id)
                                                          }}>
                                                    Delete
                                                </CButton>}
                                            </CGrid>
                                        </CGridContainer>
                                    </CCardContent>
                                </CCard>
                            )
                        })}

                    </CGrid>
                </CGrid>

                <CSnackbar open={snackBar.isOpen} autoHideDuration={6000} onClose={closeSnackbar}>
                    <CAlert onClose={closeSnackbar} severity={snackBar.severity} sx={{width: '100%'}}>
                        {snackBar.message}
                    </CAlert>
                </CSnackbar>

                <CDialog
                    maxWidth={"xs"}
                    PaperProps={{
                        style: {
                            borderRadius: 27,
                        }
                    }}
                    componentsProps={{
                        backdrop: {
                            style: {
                                backdropFilter: "blur(3px)",
                                backgroundColor: 'rgba(0,0,30,0.4)'
                            }
                        },
                    }} open={dialog.isOpen} onClose={closeUpdateDialog} fullWidth>
                    <CDialogTitle fontWeight={"700"}
                                  sx={{mb: 2}}>{dialog.isAdd ? "Add New" : "Edit"} Project</CDialogTitle>
                    <CDialogContent>
                        <CGrid container spacing={2}>
                            <CGrid item xs={12} sx={{display: 'flex', flexDirection: 'row', alignItems: 'center'}}>
                                <CTextField
                                    fullWidth
                                    error={hasValue(formDataValidationMessages.name)}
                                    helperText={formDataValidationMessages.name}
                                    label="Project Name"
                                    variant="standard"
                                    sx={{
                                        mb: 2
                                    }}
                                    value={formData.name}
                                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                        setFormData({
                                            ...formData,
                                            name: event.target.value
                                        });
                                    }}
                                />
                            </CGrid>
                            <CGrid item xs={12} sx={{display: 'flex', flexDirection: 'row', alignItems: 'center'}}>
                                <CTextField
                                    fullWidth
                                    error={hasValue(formDataValidationMessages.walletAddress)}
                                    helperText={formDataValidationMessages.walletAddress}
                                    label="Wallet Address"
                                    variant="standard"
                                    sx={{
                                        mb: 2
                                    }}
                                    value={formData.walletAddress}
                                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                        setFormData({
                                            ...formData,
                                            walletAddress: event.target.value
                                        });
                                    }}
                                />
                            </CGrid>
                        </CGrid>
                    </CDialogContent>

                    {(isUpdating) ? <CDialogActions sx={{alignItems: 'center', justifyContent: 'center'}}>
                        <CCircularProgress sx={{mb: 1}}/>
                    </CDialogActions> : <CDialogActions sx={{mb: 1}}>
                        <CButton onClick={closeUpdateDialog}>Cancel</CButton>
                        <CButton size="medium" variant="contained" onClick={() => {
                            if (dialog.isAdd) {
                                add()
                            } else {
                                update()
                            }
                        }}>{dialog.isAdd ? "Add" : "Update"}</CButton>
                    </CDialogActions>}
                </CDialog>


                <CDialog
                    maxWidth={"xs"}
                    PaperProps={{
                        style: {
                            borderRadius: 27,
                        }
                    }}
                    componentsProps={{
                        backdrop: {
                            style: {
                                backdropFilter: "blur(3px)",
                                backgroundColor: 'rgba(0,0,30,0.4)'
                            }
                        },
                    }} open={revokeDialog.isOpen} onClose={closeRevokeDialog} fullWidth>
                    <CDialogTitle fontWeight={"700"} sx={{mb: 2}}>Revoke Project Api Key</CDialogTitle>
                    <CDialogContent>
                        <CGrid container spacing={2}>
                            <CGrid item xs={12} sx={{display: 'flex', flexDirection: 'row', alignItems: 'center'}}>
                                <CBody1>
                                    By revoking your current Api-Key you have to update your app to use the new
                                    generated Api-Key.
                                    Otherwize the installed Abeld SDK in your app won't be able to connect to our
                                    services any longer.
                                </CBody1>
                            </CGrid>
                        </CGrid>
                    </CDialogContent>

                    {(isUpdating) ? <CDialogActions sx={{alignItems: 'center', justifyContent: 'center'}}>
                        <CCircularProgress sx={{mb: 1}}/>
                    </CDialogActions> : <CDialogActions sx={{mb: 1}}>
                        <CButton onClick={closeRevokeDialog}>Cancel</CButton>
                        <CButton size="medium" variant="contained" onClick={revokeApiKey}>Revoke</CButton>
                    </CDialogActions>}
                </CDialog>

                <CDialog
                    maxWidth={"xs"}
                    PaperProps={{
                        style: {
                            borderRadius: 27,
                        }
                    }}
                    componentsProps={{
                        backdrop: {
                            style: {
                                backdropFilter: "blur(3px)",
                                backgroundColor: 'rgba(0,0,30,0.4)'
                            }
                        },
                    }} open={deleteDialog.isOpen} onClose={closeDeleteDialog} fullWidth>
                    <CDialogTitle fontWeight={"700"} sx={{mb: 2}} color={"error"}>Delete Project</CDialogTitle>
                    <CDialogContent>
                        <CGrid container spacing={2}>
                            <CGrid item xs={12} sx={{display: 'flex', flexDirection: 'row', alignItems: 'center'}}>
                                <CBody1>
                                    After deleting this project from Abeld panel the installed Abeld SDK on your project
                                    will stop working with our services.
                                </CBody1>
                            </CGrid>
                        </CGrid>
                    </CDialogContent>

                    {(isUpdating) ? <CDialogActions sx={{alignItems: 'center', justifyContent: 'center'}}>
                        <CCircularProgress sx={{mb: 1}}/>
                    </CDialogActions> : <CDialogActions sx={{mb: 1}}>
                        <CButton onClick={closeDeleteDialog}>Cancel</CButton>
                        <CButton color={"error"} size="medium" variant="contained" onClick={submitDelete}>Delete</CButton>
                    </CDialogActions>}
                </CDialog>
                <CDialog
                    maxWidth={"sm"}
                    PaperProps={{
                        style: {
                            borderRadius: 27,
                        }
                    }}
                    componentsProps={{
                        backdrop: {
                            style: {
                                backdropFilter: "blur(3px)",
                                backgroundColor: 'rgba(0,0,30,0.4)'
                            }
                        },
                    }} open={sdkDialog.isOpen} onClose={closeSDKDialog} fullWidth>
                    <CDialogTitle fontWeight={"700"} sx={{mb: 2}}>SDK setup & Configuration</CDialogTitle>
                    <CDialogContent>
                        <CGrid container spacing={2}>
                            <CGrid item xs={12} sx={{display: 'flex', flexDirection: 'row', alignItems: 'center', mt: 1}}>
                                <CFormControl fullWidth>
                                    <CInputLabel id="platform-label">Platform</CInputLabel>
                                    <CSelect
                                        labelId="platform-label"
                                        id="platform"
                                        value={sdkDialog.platform}
                                        label="Platform"
                                        onChange={(event: SelectChangeEvent<unknown>) => {
                                            setSDKDialog({...sdkDialog, platform: event.target.value as string});
                                        }}
                                    >
                                        <CMenuItem value={"Flutter"}>Flutter</CMenuItem>
                                        <CMenuItem value={"Submitted"}>React Native</CMenuItem>
                                        <CMenuItem value={"iOS"}>iOS</CMenuItem>
                                        <CMenuItem value={"Android"}>Android</CMenuItem>
                                        <CMenuItem value={"React"}>React</CMenuItem>
                                        <CMenuItem value={"Vue"}>Vue</CMenuItem>
                                        <CMenuItem value={"Angular"}>Angular</CMenuItem>
                                    </CSelect>
                                </CFormControl>
                            </CGrid>
                            <CGrid item xs={12} sx={{mt: 1}}>
                                <CBody1>Add SDK to the Project:</CBody1>
                                <CBox sx={{display: "flex", mt: 1, backgroundColor: "#EEEEEE", p: 1, borderRadius: 2, alignItems: 'center'}}>
                                    <CBody2 sx={{mr: 1, opacity: 0.5, fontWeight: 700}} >
                                        $
                                    </CBody2>
                                    <CBody2 sx={{flex: 1}} >
                                        flutter pub add abeld-sdk
                                    </CBody2>
                                    <IconButton size={"small"} sx={{ml: 1}} aria-label="copy"
                                                onClick={async () => {
                                                    await navigator.clipboard.writeText("flutter pub add abeld-sdk")
                                                }}>
                                        <ContentCopyOutlined fontSize={"small"}/>
                                    </IconButton>

                                </CBox >

                            </CGrid>
                            <CGrid item xs={12} sx={{mt: 1}}>
                                <CBody1>New Instance:</CBody1>
                                <CBox sx={{display: "flex", mt: 1, backgroundColor: "#EEEEEE", p: 1, borderRadius: 2, alignItems: 'center'}}>
                                    <CBody2 sx={{flex: 1}} >
                                        ABX abx = ABX("{sdkDialog.data?.apiKey}");
                                    </CBody2>
                                    <IconButton size={"small"} sx={{ml: 1}} aria-label="copy"
                                                onClick={async () => {
                                                    await navigator.clipboard.writeText(`ABX abx = ABX("${sdkDialog.data?.apiKey}");`)
                                                }}>
                                        <ContentCopyOutlined fontSize={"small"}/>
                                    </IconButton>

                                </CBox >

                            </CGrid>
                        </CGrid>
                    </CDialogContent>

                    <CDialogActions sx={{mb: 1}}>
                        <CButton onClick={closeSDKDialog}>close</CButton>
                    </CDialogActions>
                </CDialog>

                <CDialog
                    maxWidth={"xs"}
                    PaperProps={{
                        style: {
                            borderRadius: 27,
                        }
                    }}
                    componentsProps={{
                        backdrop: {
                            style: {
                                backdropFilter: "blur(3px)",
                                backgroundColor: 'rgba(0,0,30,0.4)'
                            }
                        },
                    }} open={fundDialog.isOpen} onClose={closeFundDialog} fullWidth>
                    <CDialogTitle fontWeight={"700"} sx={{mb: 2}}>Fund Project</CDialogTitle>
                    <CDialogContent>
                        <CGrid container spacing={2}>
                            <CGrid item xs={12} sx={{display: 'flex', flexDirection: 'row', alignItems: 'center'}}>
                                <CBody1>
                                    Work in progress ...
                                </CBody1>
                            </CGrid>
                        </CGrid>
                    </CDialogContent>

                    {(isUpdating) ? <CDialogActions sx={{alignItems: 'center', justifyContent: 'center'}}>
                        <CCircularProgress sx={{mb: 1}}/>
                    </CDialogActions> : <CDialogActions sx={{mb: 1}}>
                        <CButton onClick={closeFundDialog}>Cancel</CButton>
                    </CDialogActions>}
                </CDialog>
            </>
        );
    }
)
